import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Heading from '~/components/core/TextComponents/Heading';
import { ArrowDownwardIcon } from '~/components/icons';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { Exposure } from '~/server_shared/swagger/data-contracts';

import IncomeProtectionDAScreenPayments from './IncomeProtectionDAScreenPayments/IncomeProtectionDAScreenPayments';
import IncomeProtectionSettingsForm from './IncomeProtectionSettingsForm/IncomeProtectionSettingsForm';
import { INCOME_PROTECTION_DA_CONTEXTS, IncomeProtectionDaContext } from './hooks';
import IncomeProtectionDAScreenSummary from './IncomeProtectionDAScreenSummary';
import IncomeProtectionSettingsDisplay from './IncomeProtectionSettingsDisplay';
import MedicalRecordsSection from './MedicalRecordsSection';
import TEXTS from './texts';
import type { IncomeProtectionDaSettingType, IncomeProtectionDaSummaryType } from './types';

interface IncomeProtectionDAScreenContentProps {
  setting: IncomeProtectionDaSettingType;
  summary: IncomeProtectionDaSummaryType;
  exposure: Exposure & { id: number };
  claim: ClaimPolymorph;
  viewOnly: boolean;
  onClose: () => void;
  onSettingUpdated: () => Promise<unknown> | unknown;
  isPaymentNotificationEnabled: boolean;
  isMedicalDocumentNotificationEnabled: boolean;
}

const IncomeProtectionDAScreenContentInner = ({
  setting,
  summary,
  exposure,
  claim,
  viewOnly,
  onClose,
  onSettingUpdated,
  isPaymentNotificationEnabled,
  isMedicalDocumentNotificationEnabled,
}: IncomeProtectionDAScreenContentProps) => {
  const [isPaymentSettingsFormOpen, setIsPaymentSettingsFormOpen] = useState(false);
  const closePaymentSettingsForm = () => setIsPaymentSettingsFormOpen(false);

  return (
    <div className="grid grid-rows-[auto_auto_1fr] gap-24 pt-4">
      <div className="flex items-center gap-2">
        <IconButton onClick={onClose}>
          <ArrowDownwardIcon className="rotate-[90deg]" size={20} />
        </IconButton>
        <Heading variant={Heading.TYPES.H1}>{TEXTS.screenTitle}</Heading>
      </div>
      <IncomeProtectionDAScreenSummary summary={summary} exposure={exposure} claim={claim} />
      <div className="grid grid-cols-[clamp(240px,20vw,320px)_1fr] gap-24 bg-white p-24">
        <IncomeProtectionSettingsDisplay
          setting={setting}
          onEditClick={() => setIsPaymentSettingsFormOpen(true)}
          viewOnly={viewOnly}
        />
        <div className="flex flex-col gap-24">
          <MedicalRecordsSection
            organizationId={claim.organization_id}
            claimId={claim.id}
            exposureId={exposure.id}
            onMedicalRecordsChanged={onSettingUpdated}
            viewOnly={viewOnly}
            latestCertificateEndDate={summary.medical_certificate_expiry_date}
            medicalDocumentNotification={setting.medical_document_notification}
            isMedicalDocumentNotificationEnabled={isMedicalDocumentNotificationEnabled}
          />
          <IncomeProtectionDAScreenPayments
            paymentSetting={setting}
            medicalCertificateExpiryDate={summary.medical_certificate_expiry_date}
            exposure={exposure}
            claim={claim}
            isPaymentNotificationEnabled={isPaymentNotificationEnabled}
            onNotificationUpdate={onSettingUpdated}
          />
        </div>
      </div>
      {isPaymentSettingsFormOpen ? (
        <CardDialog
          isDialog
          open
          onClose={closePaymentSettingsForm}
          trackAlt="Income Protection Payment Settings Update Dialog"
          {...IncomeProtectionSettingsForm.RECOMMENDED_CARD_DIALOG_VIEW_PROPS}
        >
          <IncomeProtectionSettingsForm
            onCancel={closePaymentSettingsForm}
            onSuccess={async () => {
              await onSettingUpdated();
              closePaymentSettingsForm();
            }}
            exposure={exposure}
            claim={claim}
            setting={setting}
          />
        </CardDialog>
      ) : null}
    </div>
  );
};

const IncomeProtectionDAScreenContent = (props: IncomeProtectionDAScreenContentProps) => (
  <IncomeProtectionDaContext.Provider value={{ context: INCOME_PROTECTION_DA_CONTEXTS.SCREEN }}>
    <IncomeProtectionDAScreenContentInner {...props} />
  </IncomeProtectionDaContext.Provider>
);

export default IncomeProtectionDAScreenContent;
